import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { HomeCategory, HomeCategoryReq, HomeData } from "../../types/homeCategoryType";
import { api } from "../../config/Api";

const API_URL = 'home/admin';

export const updateHomeCategory = createAsyncThunk<HomeCategory, { id: number; data: HomeCategory }, { rejectValue: any }>(
  'homeCategory/updateHomeCategory',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`${API_URL}/home-category/${id}`, data);
      
      return response.data;
    } catch (error: any) {
      
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data); // Return error response data if available
      } else {
        return rejectWithValue('An error occurred while updating the category.');
      }
    }
  }
);

export const fetchHomeCategories = createAsyncThunk<HomeCategory[]>(
    'homeCategory/fetchHomeCategories',
    async (_, { rejectWithValue }) => {
      try {
        const response = await api.get(`${API_URL}/home-category`);
        
        return response.data;
      } catch (error: any) {
        
        return rejectWithValue(
          error.response?.data?.message || 'Failed to fetch categories'
        );
      }
    }
  );
  
  
    export const createHomeCategories = createAsyncThunk<HomeData, HomeCategory[],{ rejectValue: { message: string } }>(
      'homeCategory/createHomeCategories',
      async (homeCategories, { rejectWithValue }) => {
        try {
          
          const response = await api.post<HomeData>('/home/categories', homeCategories);
         
          return response.data;
        } catch (error: any) {
          // Handle the error and return it to be used in rejected action
     
          const errorMessage = error.response?.data?.message || error.message || 'Failed to create home categories';
          
          return rejectWithValue(errorMessage);
        }
      }
    );
    
  interface HomeCategoryState {
    homePageData: HomeData | null;
    categories: HomeCategory[];
    loading: boolean;
    error: string | null;
    categoryUpdated:boolean;
  }
  
  const initialState: HomeCategoryState = {
    homePageData:null,
    categories: [],
    loading: true,
    error: null,
    categoryUpdated: false,
  };

  // Create the slice
const homeCategorySlice = createSlice({
    name: 'homeCategory',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      // Handle the pending state for updateHomeCategory
      builder.addCase(updateHomeCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.categoryUpdated = false;
      });
  
      // Handle the fulfilled state for updateHomeCategory
      builder.addCase(updateHomeCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categoryUpdated = true;
  
        // Find the category by ID and update it in the state
        const index = state.categories.findIndex(
          (category) => category.id === action.payload.id
        );
        if (index !== -1) {
          state.categories[index] = action.payload;
        } else {
          state.categories.push(action.payload); // If the category doesn't exist, add it
        }
      });
  
      // Handle the rejected state for updateHomeCategory
      builder.addCase(updateHomeCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  
      // Fetch home categories
      builder
        .addCase(fetchHomeCategories.pending, (state) => {
          state.loading = true;
          state.error = null;
          state.categoryUpdated = false; // Reset categoryUpdated flag
        })
        .addCase(fetchHomeCategories.fulfilled, (state, action) => {
          state.loading = false;
          state.categories = action.payload;
        })
        .addCase(fetchHomeCategories.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });


        //createHomeCategories 
        builder.addCase(createHomeCategories.pending, (state) => {
          state.loading = true;
          state.error = null;
        });
    
        builder.addCase(createHomeCategories.fulfilled, (state, action) => {
          state.loading = false;
          state.homePageData = action.payload;
        });
    
        builder.addCase(createHomeCategories.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message || 'Failed to create home categories';
        });
    },
  });
  
  export const homeCategorySliceReducer = homeCategorySlice.reducer;